import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { searchEngineers, getTalents, getTalentsBatch, ActionType, DomainType, listSearchBatches, SearchBatch, getShareLink } from '../services/api';
import { exportSavedProfilesCsv } from '../utils/exportSavedCsv';
import { SearchFilters, SoftwareTalent, SearchResponse, ParsedRequestBody, EducationLevel } from '../types';
import FilterSection from '../components/FilterSection';
import SearchResults from '../components/SearchResults';
import QuickViewModal from '../components/QuickViewModal';
import { deserializeFiltersFromUrl, updateUrlWithFilters } from '../utils/urlFilters';
import { buildSearchPayload } from '../utils/searchPayload';
import { Search, ChevronDown, SlidersHorizontal, AlertCircle, X, Check, ArrowLeft, Share, Download, Loader2 } from 'lucide-react';
import AppNavbar from '../components/AppNavbar';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import '../App.css';
import { useScrollVisibility } from '../hooks/useScrollVisibility';
import { fetchAppConfig, getDomainLabel, getDomainPlaceholder, resolveDomainCode } from '../config/appConfig';
import { trackEvent, registerSuperProperties, startTimedEvent } from '../utils/mixpanel';
import { useCurrentUser } from '../hooks/useCurrentUser';

type TabType = 'search' | 'saved' | 'viewed' | 'discarded';

// State structure for each tab
interface TabState {
  talent: SoftwareTalent[];
  pagination: SearchResponse['pagination'] | undefined;
  scrollPosition: number;
  loaded: boolean;
  error: string | null;
  // Search-specific state
  highlightTerms?: string[];
  // Store filters hash to detect if search needs to be re-run
  filtersHash?: string;
}

const createInitialTabState = (): TabState => ({
  talent: [],
  pagination: undefined,
  scrollPosition: 0,
  loaded: false,
  error: null,
});

// Simple hash function for filters to detect changes
const hashFilters = (filters: SearchFilters): string => {
  const { page, pageSize, ...rest } = filters;
  return JSON.stringify(rest);
};

// A fresh search-bar query starts from a clean slate: keep only the query itself,
// domain, pagination and batch/talent scoping; drop ALL structured facets so the
// new query's parse (returned as appliedFilters) fully determines them. Without
// this, facets from a previous query (e.g. excludeViewed, skills, companies) leak
// into the next search via the backend's filter-merge.
const resetParsedFilters = (filters: SearchFilters): SearchFilters => ({
  searchQuery: filters.searchQuery,
  domain: filters.domain,
  page: 1,
  pageSize: filters.pageSize,
  packetIds: filters.packetIds,
  talentIds: filters.talentIds,
});

// Module-level flag to deduplicate the initial fetch within the same synchronous mount cycle.
// Reset synchronously on unmount so tenant-switch remounts always trigger a fresh fetch.
let initialFetchStarted = false;

const SearchPage: React.FC = () => {
  const { role } = useCurrentUser();
  const canDownload = role === 'admin' || role === 'superadmin';

  // Domain config — fetched from /api/config on mount
  const [availableDomains, setAvailableDomains] = useState<string[]>(['SWE']);

  // Aggregate domain counts from batches API (used to derive selectable domains)
  const [allBatchesDomainCounts, setAllBatchesDomainCounts] = useState<Record<string, number>>({});

  const domainsLoadedRef = useRef(false);
  const [domainsReady, setDomainsReady] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    if (!token) return;
    fetchAppConfig().then(domains => {
      setAvailableDomains(domains);
      domainsLoadedRef.current = true;
      setDomainsReady(true);
    });
  }, []);

  // Track navbar visibility based on scroll direction
  const isNavbarVisible = useScrollVisibility({ threshold: 10 });
  
  const location = useLocation();
  const [activeTab, setActiveTab] = useState<TabType>(() => {
    const params = new URLSearchParams(location.search);
    const urlTab = params.get('tab');
    if (urlTab === 'saved' || urlTab === 'viewed' || urlTab === 'discarded') return urlTab;
    const s = (location.state as any)?.tab;
    return (s === 'saved' || s === 'viewed' || s === 'discarded') ? s : 'search';
  });
  const activeTabRef = useRef<TabType>(activeTab); // Ref to track current tab for async callbacks
  
  // Update ref when activeTab changes
  useEffect(() => {
    activeTabRef.current = activeTab;
  }, [activeTab]);
  
  // Tab counts - stored per domain for accurate single-domain refresh
  // Shape: { SWE: { saved: 5, viewed: 10, discarded: 2 }, STEM: { ... } }
  const [domainCounts, setDomainCounts] = useState<Record<string, Record<string, number>>>({});

  // Helper to update a single domain+tab count
  const updateDomainCount = useCallback((domain: string, tab: string, count: number) => {
    setDomainCounts(prev => ({
      ...prev,
      [domain]: { ...prev[domain], [tab]: count },
    }));
  }, []);

  // Derived total counts for navbar
  const savedCount = Object.values(domainCounts).reduce((s, d) => s + (d.saved ?? 0), 0);
  const viewedCount = Object.values(domainCounts).reduce((s, d) => s + (d.viewed ?? 0), 0);
  const discardedCount = Object.values(domainCounts).reduce((s, d) => s + (d.discarded ?? 0), 0);
  
  // Track last page loaded per tab per domain (to fix pagination with frontend filtering)
  // Shape: { saved: { SWE: 1, STEM: 1 }, viewed: { ... }, discarded: { ... } }
  const [lastPageLoaded, setLastPageLoaded] = useState<Record<string, Record<string, number>>>({
    saved: {}, viewed: {}, discarded: {},
  });
  
  // Track total pages per tab per domain
  const [totalPages, setTotalPages] = useState<Record<string, Record<string, number>>>({
    saved: {}, viewed: {}, discarded: {},
  });
  
  // Initialize filters from URL params or default
  const [filters, setFilters] = useState<SearchFilters>(() => {
    const urlFilters = deserializeFiltersFromUrl();
    if (Object.keys(urlFilters).length > 2) {
      return urlFilters;
    }
    return { page: 1, pageSize: 18 };
  });

  const [talent, setTalent] = useState<SoftwareTalent[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [pagination, setPagination] = useState<SearchResponse['pagination']>();
  const [error, setError] = useState<string | null>(null);
  const [hasInteracted, setHasInteracted] = useState(false);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);
  const [shareId, setShareId] = useState<string | null>(null);
  const [downloadingSavedCsv, setDownloadingSavedCsv] = useState(false);
  // Store parsed request body from first page (used for page 2+ to avoid re-running GPT parsing)
  const [parsedRequestBody, setParsedRequestBody] = useState<ParsedRequestBody | null>(null);
  // Track the highlight terms for current results (skill names, programming languages from parsed filters)
  // Only update when results are received to avoid highlighting stale results
  const [resultsHighlightTerms, setResultsHighlightTerms] = useState<string[]>([]);
  // Track saved/viewed/discarded IDs for UI state
  const [savedIds, setSavedIds] = useState<Set<number>>(new Set());
  const [viewedIds, setViewedIds] = useState<Set<number>>(new Set());
  const [discardedIds, setDiscardedIds] = useState<Set<number>>(new Set());

  // Debounce timer ref for auto-search
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Custom domain dropdown state
  const [domainDropdownOpen, setDomainDropdownOpen] = useState(false);
  const domainDropdownDesktopRef = useRef<HTMLDivElement>(null);
  const domainDropdownMobileRef = useRef<HTMLDivElement>(null);

  // Batch dropdown state
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_batches, setBatches] = useState<SearchBatch[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_totalUniqueCount, setTotalUniqueCount] = useState(0);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [selectedBatch, setSelectedBatch] = useState<SearchBatch | null>(null);
  const selectedBatchRef = useRef<SearchBatch | null>(null);
  const allPacketIdsRef = useRef<number[]>([]);
  const [batchDropdownOpen, setBatchDropdownOpen] = useState(false);
  const batchDropdownRef = useRef<HTMLDivElement>(null);

  // Selectable domains = enabled domains (from config) that have >0 talents in batch scope
  const selectableDomains = useMemo(() => {
    const counts = selectedBatch ? (selectedBatch.domainCounts || {}) : allBatchesDomainCounts;
    const filtered = availableDomains.filter(d => (counts[d] ?? counts[d.toUpperCase()] ?? 0) > 0);
    // Fallback: if no domains match (e.g. stale data), show all enabled domains
    return filtered.length > 0 ? filtered : availableDomains;
  }, [selectedBatch, allBatchesDomainCounts, availableDomains]);
  // Show domain selector whenever tenant has multiple domains enabled (even if current
  // batch only has 1 domain — user still sees which domain is active). Dropdown options
  // are filtered by selectableDomains so only valid choices appear.
  const multiDomain = availableDomains.length > 1;

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      const insideDesktop = domainDropdownDesktopRef.current?.contains(target);
      const insideMobile = domainDropdownMobileRef.current?.contains(target);
      if (!insideDesktop && !insideMobile) {
        setDomainDropdownOpen(false);
      }
    };
    if (domainDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [domainDropdownOpen]);

  // Click-outside for batch dropdown
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (!batchDropdownRef.current?.contains(e.target as Node)) {
        setBatchDropdownOpen(false);
      }
    };
    if (batchDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [batchDropdownOpen]);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // Batches are tenant-scoped (not domain-scoped) — fetched once in the initial data effect

  // Talent displayed in the QuickViewModal (card click popup)
  const [quickViewTalent, setQuickViewTalent] = useState<SoftwareTalent | null>(null);
  
  // Track the active domain filter when viewing a talent (for prev/next navigation on action tabs)
  const [activeDomainFilter, setActiveDomainFilter] = useState<string>(availableDomains[0] || 'SWE');

  // Per-tab state storage (persists across tab switches)
  const tabStatesRef = useRef<Record<TabType, TabState>>({
    search: createInitialTabState(),
    saved: createInitialTabState(),
    viewed: createInitialTabState(),
    discarded: createInitialTabState(),
  });

  // Map tab type to action type for API
  const tabToActionType = (tab: TabType): ActionType | null => {
    switch (tab) {
      case 'saved': return 'SAVED';
      case 'viewed': return 'VIEWED';
      case 'discarded': return 'DISCARDED';
      default: return null;
    }
  };

  // Fetch tab data in background and store in ref (doesn't affect current view)
  // If domain is specified, only fetches that domain and merges with cached data for other domains
  // If domain is not specified, fetches all available domains
  const refreshTabDataInBackground = useCallback(async (tab: TabType, domain?: DomainType) => {
    const actionType = tabToActionType(tab);
    if (!actionType) return;
    const tabKey = tab as string; // 'saved' | 'viewed' | 'discarded'

    try {
      const tabState = tabStatesRef.current[tab];
      
      if (domain) {
        // Single domain refresh - only fetch the specified domain
        const response = await getTalents(actionType, domain, 1, 18);
        
        if (response.success) {
          // Filter out discarded developers from Saved and Viewed tabs
          const filteredEngineers = (tab === 'saved' || tab === 'viewed')
            ? response.engineers.filter(e => !e.isDiscarded)
            : response.engineers;
          const newEngineers = filteredEngineers.map(e => ({ ...e, domain }));
          
          // Keep existing engineers from all other domains (also filter discarded)
          const otherDomainEngineers = tabState.talent.filter(e => 
            e.domain !== domain && (tab === 'discarded' || !e.isDiscarded)
          );
          const combinedEngineers = [...otherDomainEngineers, ...newEngineers];
          
          // Update per-domain count for the refreshed domain
          const newDomainCount = response.pagination?.total ?? 0;
          updateDomainCount(domain, tabKey, newDomainCount);
          
          const combinedPagination = {
            page: 1,
            pageSize: availableDomains.length * 18,
            total: combinedEngineers.length, // Will be updated on next full refresh
            totalPages: Math.max(
              response.pagination?.totalPages ?? 1,
              tabState.pagination?.totalPages ?? 1
            ),
          };

          const newIds = combinedEngineers.map(e => e.id).join(',');
          const oldIds = tabState.talent.map(e => e.id).join(',');
          const hasChanged = newIds !== oldIds;

          // Update tab state ref
          tabState.talent = combinedEngineers;
          tabState.pagination = combinedPagination;
          tabState.loaded = true;
          tabState.error = null;

          // If we're currently on this tab and data changed, update the current state
          if (activeTabRef.current === tab && hasChanged) {
            setTalent(combinedEngineers);
            setPagination(combinedPagination);
          }
          
          // Reset page tracking for this domain (since we refreshed page 1)
          setLastPageLoaded(prev => ({
            ...prev,
            [tabKey]: { ...prev[tabKey], [domain]: 1 },
          }));
          setTotalPages(prev => ({
            ...prev,
            [tabKey]: { ...prev[tabKey], [domain]: response.pagination?.totalPages ?? 1 },
          }));
        }
      } else {
        // Full refresh - 1 batched call for all domains
        const multiRes = await getTalentsBatch(actionType, availableDomains as DomainType[], 1, 18);
        const domainMap = multiRes.results;

        const anySuccess = Object.values(domainMap).some(r => r.success);
        if (anySuccess) {
          const filterDiscarded = (engineers: SoftwareTalent[]) =>
            (tab === 'saved' || tab === 'viewed') ? engineers.filter(e => !e.isDiscarded) : engineers;

          const combinedEngineers: SoftwareTalent[] = [];
          let totalCount = 0;
          let maxTotalPages = 1;
          const newPageTracking: Record<string, number> = {};

          availableDomains.forEach(d => {
            const res = domainMap[d];
            if (!res) return;
            const domainCount = res.pagination?.total ?? 0;
            totalCount += domainCount;
            const domainTotalPages = res.pagination?.totalPages ?? 1;
            if (domainTotalPages > maxTotalPages) maxTotalPages = domainTotalPages;
            newPageTracking[d] = domainTotalPages;
            updateDomainCount(d, tabKey, domainCount);
            const engineers = filterDiscarded(res.engineers).map(e => ({ ...e, domain: d }));
            combinedEngineers.push(...engineers);
          });
          
          const combinedPagination = {
            page: 1,
            pageSize: availableDomains.length * 18,
            total: totalCount,
            totalPages: maxTotalPages,
          };

          const newIds = combinedEngineers.map(e => e.id).join(',');
          const oldIds = tabState.talent.map(e => e.id).join(',');
          const hasChanged = newIds !== oldIds || tabState.pagination?.total !== totalCount;

          // Always update tab state ref
          tabState.talent = combinedEngineers;
          tabState.pagination = combinedPagination;
          tabState.loaded = true;
          tabState.error = null;

          // If we're currently on this tab and data changed, update the current state
          if (activeTabRef.current === tab && hasChanged) {
            setTalent(combinedEngineers);
            setPagination(combinedPagination);
          }
          
          // Reset page tracking for all domains (since we refreshed page 1 for all)
          const initPages = Object.fromEntries(availableDomains.map(d => [d, 1]));
          setLastPageLoaded(prev => ({ ...prev, [tabKey]: initPages }));
          setTotalPages(prev => ({ ...prev, [tabKey]: newPageTracking }));
        }
      }
    } catch {
      // Silently fail - will reload when user switches to tab
      tabStatesRef.current[tab].loaded = false;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableDomains, updateDomainCount]);

  // Handlers for save/discard/restore actions
  // Note: Counts are updated from API response in refreshTabDataInBackground (pagination.total)
  // Domain parameter allows single-domain refresh to reduce API calls
  const handleSave = useCallback((talentId: number, domain?: DomainType) => {
    setSavedIds(prev => new Set(prev).add(talentId));
    setViewedIds(prev => new Set(prev).add(talentId));
    // Remove from discarded if it was discarded
    setDiscardedIds(prev => {
      const next = new Set(prev);
      next.delete(talentId);
      return next;
    });
    // Refresh affected tabs
    refreshTabDataInBackground('saved', domain);
    refreshTabDataInBackground('viewed', domain);
    refreshTabDataInBackground('discarded', domain);
    trackEvent('Candidate Saved', { candidateId: talentId, domain });
  }, [refreshTabDataInBackground]);

  const handleDiscard = useCallback((talentId: number, domain?: DomainType) => {
    // Check which tabs will be affected before updating state
    const wasInSaved = savedIds.has(talentId);
    const wasInViewed = viewedIds.has(talentId);

    setDiscardedIds(prev => new Set(prev).add(talentId));
    // Remove from saved and viewed
    setSavedIds(prev => {
      const next = new Set(prev);
      next.delete(talentId);
      return next;
    });
    setViewedIds(prev => {
      const next = new Set(prev);
      next.delete(talentId);
      return next;
    });
    // Refresh affected tabs for the specific domain only
    refreshTabDataInBackground('discarded', domain);
    if (wasInSaved) {
      refreshTabDataInBackground('saved', domain);
    }
    if (wasInViewed) {
      refreshTabDataInBackground('viewed', domain);
    }
    // On search tab, keep the card but show it greyed out (handled by discardedIds state)
    // On other tabs (saved/viewed), remove the card from the list
    if (activeTab !== 'discarded' && activeTab !== 'search') {
      setTalent(prev => prev.filter(t => t.id !== talentId));
    }
    trackEvent('Candidate Discarded', { candidateId: talentId, domain });
  }, [activeTab, savedIds, viewedIds, refreshTabDataInBackground]);

  const handleRestore = useCallback((talentId: number, domain?: DomainType) => {
    setDiscardedIds(prev => {
      const next = new Set(prev);
      next.delete(talentId);
      return next;
    });
    // Refresh discarded tab for the specific domain only
    refreshTabDataInBackground('discarded', domain);
    // Remove from current talent list if on discarded tab
    if (activeTab === 'discarded') {
      setTalent(prev => prev.filter(t => t.id !== talentId));
    }
    trackEvent('Candidate Restored', { candidateId: talentId, domain });
  }, [activeTab, refreshTabDataInBackground]);

  const handleUnsave = useCallback((talentId: number, domain?: DomainType) => {
    setSavedIds(prev => {
      const next = new Set(prev);
      next.delete(talentId);
      return next;
    });
    // Refresh saved tab for the specific domain only
    refreshTabDataInBackground('saved', domain);
    // Remove from current talent list if on saved tab
    if (activeTab === 'saved') {
      setTalent(prev => prev.filter(t => t.id !== talentId));
    }
    trackEvent('Candidate Unsaved', { candidateId: talentId, domain });
  }, [activeTab, refreshTabDataInBackground]);

  const handleView = useCallback((talentId: number, domain?: DomainType) => {
    // Skip if already viewed or if developer is discarded (no point viewing discarded devs)
    if (viewedIds.has(talentId) || discardedIds.has(talentId)) {
      return;
    }
    setViewedIds(prev => new Set(prev).add(talentId));
    // Refresh viewed tab for the specific domain only
    refreshTabDataInBackground('viewed', domain);
  }, [viewedIds, discardedIds, refreshTabDataInBackground]);

  // Load tab counts and first page of data on mount
  // Only fetches page 1 with normal pageSize - IDs are populated incrementally from:
  // 1. Search API responses (isSaved/isViewed/isDiscarded flags)
  // 2. Action tab data as pages are loaded
  const fetchActionData = useCallback(async () => {
    const pageSize = 18;
    const actionTypes: ActionType[] = ['SAVED', 'VIEWED', 'DISCARDED'];
    const tabKeys = ['saved', 'viewed', 'discarded'] as const;

    try {
      // 3 calls instead of 10×3=30: one per action type, all domains batched
      const [savedRes, viewedRes, discardedRes] = await Promise.all(
        actionTypes.map(action => getTalentsBatch(action, availableDomains as DomainType[], 1, pageSize))
      );
      const multiResults = [savedRes.results, viewedRes.results, discardedRes.results];

      const domainCount = availableDomains.length;
      const newCounts: Record<string, Record<string, number>> = {};
      const newLastPage: Record<string, Record<string, number>> = {};
      const newTotalPages: Record<string, Record<string, number>> = {};
      const allEngineers: SoftwareTalent[] = [];

      tabKeys.forEach((tabKey, actionIdx) => {
        const domainMap = multiResults[actionIdx];
        const combinedEngineers: SoftwareTalent[] = [];
        let totalCount = 0;
        let maxTotalPages = 1;
        const pageTracking: Record<string, number> = {};
        const initPages: Record<string, number> = {};

        availableDomains.forEach(d => {
          const res = domainMap[d];
          if (!res) return;
          const count = res.pagination?.total ?? 0;
          const tp = res.pagination?.totalPages ?? 1;
          totalCount += count;
          if (tp > maxTotalPages) maxTotalPages = tp;
          pageTracking[d] = tp;
          initPages[d] = 1;

          if (!newCounts[d]) newCounts[d] = {};
          newCounts[d][tabKey] = count;

          const engineers = (tabKey === 'saved' || tabKey === 'viewed')
            ? res.engineers.filter(e => !e.isDiscarded)
            : res.engineers;
          combinedEngineers.push(...engineers.map(e => ({ ...e, domain: d })));
          allEngineers.push(...res.engineers);
        });

        newLastPage[tabKey] = initPages;
        newTotalPages[tabKey] = pageTracking;

        const anySuccess = Object.values(domainMap).some(r => r.success);
        if (anySuccess && !tabStatesRef.current[tabKey].loaded) {
          tabStatesRef.current[tabKey] = {
            talent: combinedEngineers,
            pagination: {
              page: 1,
              pageSize: pageSize * domainCount,
              total: totalCount,
              totalPages: maxTotalPages,
            },
            scrollPosition: 0,
            loaded: true,
            error: null,
          };
        }
      });

      // Merge domain counts (preserve counts for domains not covered by this fetch)
      setDomainCounts(prev => {
        const merged = { ...prev };
        for (const [domain, tabs] of Object.entries(newCounts)) {
          merged[domain] = { ...merged[domain], ...tabs };
        }
        return merged;
      });

      // Merge backend flags into existing sets (don't replace — search results may have populated them first)
      const newSavedIds = new Set<number>();
      const newViewedIds = new Set<number>();
      const newDiscardedIds = new Set<number>();
      allEngineers.forEach(e => {
        if (e.isSaved) newSavedIds.add(e.id);
        if (e.isViewed) newViewedIds.add(e.id);
        if (e.isDiscarded) newDiscardedIds.add(e.id);
      });
      setSavedIds(prev => {
        const merged = new Set(prev);
        newSavedIds.forEach(id => merged.add(id));
        return merged;
      });
      setViewedIds(prev => {
        const merged = new Set(prev);
        newViewedIds.forEach(id => merged.add(id));
        return merged;
      });
      setDiscardedIds(prev => {
        const merged = new Set(prev);
        newDiscardedIds.forEach(id => merged.add(id));
        return merged;
      });

      // Initialize page tracking for all action tabs
      setLastPageLoaded(newLastPage);
      setTotalPages(newTotalPages);
    } catch {
      // Silently fail - counts will show 0
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableDomains, updateDomainCount]);

  // Single effect for all initial data fetching (search + action tabs)
  // Waits for domain config to load so we search the correct domain
  useEffect(() => {
    if (!domainsReady) return;
    if (initialFetchStarted) return;
    initialFetchStarted = true;
    let cancelled = false;

    // Fetch batches (tenant-scoped, not domain-scoped) before running search
    listSearchBatches().then(({ batches: fetched, totalUniqueCount: total, domainCounts: aggDomainCounts }) => {
      if (cancelled) return;
      setBatches(fetched);
      setTotalUniqueCount(total);
      setAllBatchesDomainCounts(aggDomainCounts);
      allPacketIdsRef.current = fetched.map(b => b.id);

      // Determine selectable domains based on batch data ∩ tenant-enabled domains
      const selectable = availableDomains.filter(d => (aggDomainCounts[d] ?? aggDomainCounts[d.toUpperCase()] ?? 0) > 0);
      const defaultDomain = selectable[0] || availableDomains[0] || 'SWE';
      const resolvedDomain = filters.domain ? resolveDomainCode(filters.domain, selectable) : undefined;
      const initialFilters = { ...filters, domain: resolvedDomain || defaultDomain, page: 1 };
      batchDomainRef.current = initialFilters.domain;
      setFilters(initialFilters);
      updateUrlWithFilters(initialFilters, true);

      if (activeTab === 'search') {
        performSearch(initialFilters);
      } else {
        loadTabData(activeTab, 1);
      }
      fetchActionData();
    }).catch(() => {
      if (cancelled) return;
      setBatches([]);
      setTotalUniqueCount(0);
      setAllBatchesDomainCounts({});
      allPacketIdsRef.current = [];
      const defaultDomain = availableDomains[0] || 'SWE';
      const resolvedDomain = filters.domain ? resolveDomainCode(filters.domain, availableDomains) : undefined;
      const initialFilters = { ...filters, domain: resolvedDomain || defaultDomain, page: 1 };
      batchDomainRef.current = initialFilters.domain;
      setFilters(initialFilters);
      updateUrlWithFilters(initialFilters, true);
      if (activeTab === 'search') {
        performSearch(initialFilters);
      } else {
        loadTabData(activeTab, 1);
      }
      fetchActionData();
    });

    return () => {
      cancelled = true;
      initialFetchStarted = false;
    };
  }, [domainsReady]); // eslint-disable-line react-hooks/exhaustive-deps

  // When domain changes, re-run search (batches don't change — they're domain-independent)
  const batchDomainRef = useRef(filters.domain);
  useEffect(() => {
    if (!domainsLoadedRef.current) return;
    if (filters.domain === batchDomainRef.current) return;
    batchDomainRef.current = filters.domain;
    if (activeTabRef.current === 'search') {
      performSearch({ ...filters, page: 1 });
    } else {
      loadTabData(activeTabRef.current as TabType, 1);
    }
  }, [filters.domain]); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-fetch action tab data when availableDomains changes AFTER initial load.
  const domainsFetchedOnceRef = useRef(false);
  const fetchActionDataRef = useRef(fetchActionData);
  useEffect(() => { fetchActionDataRef.current = fetchActionData; });
  useEffect(() => {
    if (!domainsLoadedRef.current) return;
    // Skip the first trigger (handled by initial fetch effect above).
    // Reset on cleanup so React Strict Mode double-invoke doesn't bypass this guard.
    if (!domainsFetchedOnceRef.current) {
      domainsFetchedOnceRef.current = true;
      return () => { domainsFetchedOnceRef.current = false; };
    }
    (['saved', 'viewed', 'discarded'] as const).forEach(tab => {
      tabStatesRef.current[tab].loaded = false;
    });
    fetchActionDataRef.current();
  }, [availableDomains]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load data for action tabs (saved, viewed, discarded) - fetches all available domains
  const loadTabData = useCallback(async (tab: TabType, page: number = 1) => {
    const actionType = tabToActionType(tab);
    if (!actionType) return;
    const tabKey = tab as string;

    setLoading(true);
    setError(null);

    try {
      // 1 batched call for all domains
      const multiRes = await getTalentsBatch(actionType, availableDomains as DomainType[], page, 18);
      const domainMap = multiRes.results;

      const stillOnSameTab = activeTabRef.current === tab;

      let combinedEngineers: SoftwareTalent[] = [];
      let totalCount = 0;
      let maxTotalPages = 1;
      const pageTracking: Record<string, number> = {};
      const initPages: Record<string, number> = {};

      availableDomains.forEach(d => {
        const res = domainMap[d];
        if (!res) return;
        const count = res.pagination?.total ?? 0;
        const tp = res.pagination?.totalPages ?? 1;
        totalCount += count;
        if (tp > maxTotalPages) maxTotalPages = tp;
        pageTracking[d] = tp;
        initPages[d] = 1;
        updateDomainCount(d, tabKey, count);
        const engineers = res.engineers.map(e => ({ ...e, domain: d }));
        combinedEngineers.push(...engineers);
      });

      if (tab === 'viewed' || tab === 'saved') {
        combinedEngineers = combinedEngineers.filter(e => !e.isDiscarded);
      }

      const anySuccess = Object.values(domainMap).some(r => r.success);
      if (anySuccess) {
        // Update ALL Sets based on backend flags (source of truth)
        const newSavedIds = new Set<number>();
        const newViewedIds = new Set<number>();
        const newDiscardedIds = new Set<number>();
        
        combinedEngineers.forEach(e => {
          if (e.isSaved) newSavedIds.add(e.id);
          if (e.isViewed) newViewedIds.add(e.id);
          if (e.isDiscarded) newDiscardedIds.add(e.id);
        });
        
        // Merge with existing IDs (page 1 replaces for current tab type, but always merges cross-tab flags)
        setSavedIds(prev => {
          const merged = page === 1 && tab === 'saved' ? new Set(newSavedIds) : new Set(prev);
          newSavedIds.forEach(id => merged.add(id));
          return merged;
        });
        setViewedIds(prev => {
          const merged = page === 1 && tab === 'viewed' ? new Set(newViewedIds) : new Set(prev);
          newViewedIds.forEach(id => merged.add(id));
          return merged;
        });
        setDiscardedIds(prev => {
          const merged = page === 1 && tab === 'discarded' ? new Set(newDiscardedIds) : new Set(prev);
          newDiscardedIds.forEach(id => merged.add(id));
          return merged;
        });
        
        // Save tab state for restoration when switching tabs (always update ref)
        const existingTabTalent = tabStatesRef.current[tab].talent;
        const existingIds = new Set(existingTabTalent.map(t => t.id));
        const newTalent = combinedEngineers.filter(e => !existingIds.has(e.id));
        tabStatesRef.current[tab] = {
          talent: page === 1 ? combinedEngineers : [...existingTabTalent, ...newTalent],
          pagination: {
            page,
            pageSize: availableDomains.length * 18,
            total: totalCount,
            totalPages: maxTotalPages,
          },
          scrollPosition: 0,
          loaded: true,
          error: null,
        };
        
        // Only update display state if we're still on the same tab
        if (stillOnSameTab) {
          if (page === 1) {
            setTalent(combinedEngineers);
          } else {
            // Append new talent (deduplicate by id)
            setTalent(prev => {
              const existingIds = new Set(prev.map(t => t.id));
              const newTalent = combinedEngineers.filter(e => !existingIds.has(e.id));
              return [...prev, ...newTalent];
            });
          }
          
          // Combined pagination (simplified - shows total from all domains)
          setPagination({
            page,
            pageSize: availableDomains.length * 18,
            total: totalCount,
            totalPages: maxTotalPages,
          });
          setResultsHighlightTerms([]);
          
          // Update page tracking for action tabs (reset on page 1)
          if (page === 1) {
            setLastPageLoaded(prev => ({ ...prev, [tabKey]: initPages }));
            setTotalPages(prev => ({ ...prev, [tabKey]: pageTracking }));
          }
        }
      } else {
        // Only update error state if still on same tab
        if (stillOnSameTab) {
          setError(`Failed to load ${tab} candidates`);
          if (page === 1) {
            setTalent([]);
            setPagination(undefined);
          }
        }
      }
    } catch (err: unknown) {
      // Only update error state if still on same tab
      if (activeTabRef.current === tab) {
        setError(err instanceof Error ? err.message : `Failed to load ${tab} candidates`);
        if (page === 1) {
          setTalent([]);
          setPagination(undefined);
        }
      }
    } finally {
      // Only clear loading if still on same tab
      if (activeTabRef.current === tab) {
        setLoading(false);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableDomains, updateDomainCount]);

  // Save current tab state to ref
  const saveCurrentTabState = useCallback(() => {
    const currentState = tabStatesRef.current[activeTab];
    currentState.talent = talent;
    currentState.pagination = pagination;
    currentState.scrollPosition = window.scrollY;
    currentState.loaded = talent.length > 0 || currentState.loaded;
    currentState.error = error;
    if (activeTab === 'search') {
      currentState.highlightTerms = resultsHighlightTerms;
    }
  }, [activeTab, talent, pagination, error, resultsHighlightTerms]);

  // Restore tab state from ref
  const restoreTabState = useCallback((tab: TabType) => {
    const savedState = tabStatesRef.current[tab];
    setTalent(savedState.talent);
    setPagination(savedState.pagination);
    setError(savedState.error);
    // Clear loading state when restoring (previous tab's API call may still be in progress)
    setLoading(false);
    setLoadingMore(false);
    if (tab === 'search') {
      setResultsHighlightTerms(savedState.highlightTerms || []);
    } else {
      setResultsHighlightTerms([]);
    }
    // Restore scroll position after render
    requestAnimationFrame(() => {
      window.scrollTo(0, savedState.scrollPosition);
    });
  }, []);

  const handleTabChange = useCallback((tab: TabType) => {
    if (tab === activeTab && !quickViewTalent) return;

    trackEvent('Tab Switched', { fromTab: activeTab, toTab: tab });

    saveCurrentTabState();
    setLoadingMore(false);
    setQuickViewTalent(null);
    setActiveTab(tab);

    const params = new URLSearchParams(window.location.search);
    if (tab === 'search') {
      params.delete('tab');
    } else {
      params.set('tab', tab);
    }
    const qs = params.toString();
    window.history.replaceState({}, '', qs ? `${window.location.pathname}?${qs}` : window.location.pathname);
  }, [activeTab, quickViewTalent, saveCurrentTabState]);
  
  // Track previous tab for tab change detection
  const prevTabRef = useRef<TabType | null>(null);

  // Load more for action tabs - fetches only the specified domain
  // Uses page tracking to correctly handle frontend filtering
  const loadMoreTabData = useCallback(async (domain: string) => {
    if (loading || loadingMore) return;
    const currentTab = activeTabRef.current;
    if (currentTab === 'search') return; // Handled by regular loadMore
    if (currentTab !== 'saved' && currentTab !== 'viewed' && currentTab !== 'discarded') return;
    
    // Get current page and total pages for this domain/tab
    const currentPage = lastPageLoaded[currentTab]?.[domain] ?? 1;
    const maxPages = totalPages[currentTab]?.[domain] ?? 1;
    
    // No more to load if we've loaded all pages
    if (currentPage >= maxPages) return;
    
    const pageSize = 18;
    const nextPage = currentPage + 1;

    setLoadingMore(true);
    const actionType = tabToActionType(currentTab);
    if (!actionType) {
      setLoadingMore(false);
      return;
    }

    try {
      // Fetch only the specified domain
      const response = await getTalents(actionType, domain, nextPage, pageSize);
      
      // Check if we're still on the same tab (user may have switched during API call)
      if (activeTabRef.current !== currentTab) {
        return; // Discard results if tab changed
      }
      
      if (response.success) {
        // Add domain field to each talent
        let engineers = response.engineers.map(e => ({ ...e, domain }));
        
        // Update ALL Sets based on backend flags (source of truth)
        engineers.forEach(e => {
          if (e.isSaved) setSavedIds(prev => new Set(prev).add(e.id));
          if (e.isViewed) setViewedIds(prev => new Set(prev).add(e.id));
          if (e.isDiscarded) setDiscardedIds(prev => new Set(prev).add(e.id));
        });
        
        // Filter out discarded developers from Viewed and Saved tabs
        if (currentTab === 'viewed' || currentTab === 'saved') {
          engineers = engineers.filter(e => !e.isDiscarded);
        }
        
        // Append new talent to existing list (deduplicate by id)
        setTalent(prev => {
          const existingIds = new Set(prev.map(t => t.id));
          const newTalent = engineers.filter(e => !existingIds.has(e.id));
          return [...prev, ...newTalent];
        });
        
        // Update per-domain count and page tracking
        const newTotal = response.pagination?.total ?? 0;
        const newTotalPages = response.pagination?.totalPages ?? 1;
        updateDomainCount(domain, currentTab, newTotal);
        
        // Update page tracking
        setLastPageLoaded(prev => ({
          ...prev,
          [currentTab]: {
            ...prev[currentTab],
            [domain]: nextPage,
          },
        }));
        setTotalPages(prev => ({
          ...prev,
          [currentTab]: {
            ...prev[currentTab],
            [domain]: newTotalPages,
          },
        }));
      }
    } catch {
      // Keep existing results on failure
    } finally {
      // Always clear loadingMore (even if tab changed, we need to reset the spinner)
      setLoadingMore(false);
    }
  }, [loading, loadingMore, lastPageLoaded, totalPages, updateDomainCount]);

  const performSearch = useCallback(async (searchFilters: SearchFilters, signal?: AbortSignal) => {
    setLoading(true);
    setError(null);
    // Clear parsed request body on new search (page 1)
    setParsedRequestBody(null);
    
    // Note: Filters clearing is handled in the search input onChange handler
    // When user types, filters are cleared there
    // When user manually adds a filter via panel, filters are preserved here
    
    try {
      const payload = buildSearchPayload(searchFilters);
      if (selectedBatchRef.current) {
        payload.packetIds = [selectedBatchRef.current.id];
      } else if (allPacketIdsRef.current.length > 0) {
        payload.packetIds = allPacketIdsRef.current;
      }
      const response = await searchEngineers(payload);
      // Skip if aborted or user switched away from search tab
      if (signal?.aborted || activeTabRef.current !== 'search') return;
      if (response.success) {
        // Show all engineers including discarded (discarded will be greyed out)
        setTalent(response.engineers);
        setPagination(response.pagination);
        
        // Update saved/viewed/discarded IDs from response
        const newSavedIds = new Set<number>();
        const newViewedIds = new Set<number>();
        const newDiscardedIds = new Set<number>();
        response.engineers.forEach(e => {
          if (e.isSaved) newSavedIds.add(e.id);
          if (e.isViewed) newViewedIds.add(e.id);
          if (e.isDiscarded) newDiscardedIds.add(e.id);
        });
        setSavedIds(prev => {
          const merged = new Set(prev);
          newSavedIds.forEach(id => merged.add(id));
          return merged;
        });
        setViewedIds(prev => {
          const merged = new Set(prev);
          newViewedIds.forEach(id => merged.add(id));
          return merged;
        });
        setDiscardedIds(prev => {
          const merged = new Set(prev);
          newDiscardedIds.forEach(id => merged.add(id));
          return merged;
        });
        
        // Build highlight terms from parsed filters - only skills and programming languages
        // These are highlighted in dev card summary and skills section only
        const newHighlightTerms: string[] = [];
        const parsed = response.debugger?.requestBody;
        if (parsed) {
          // Add skill names - format is always [{name: "Python"}]
          parsed.skills?.forEach(s => {
            if (s.name) {
              newHighlightTerms.push(s.name);
            }
          });
          // Add programming languages
          parsed.programmingLanguages?.forEach((l: string) => newHighlightTerms.push(l));
          // Add raw query if GPT couldn't parse it (for Elasticsearch phrase matching)
          if (parsed.query?.trim()) {
            newHighlightTerms.push(parsed.query.trim());
          }
        }
        // Also add manually selected skills from UI (for non-semantic searches)
        searchFilters.skills?.forEach(s => s.name && newHighlightTerms.push(s.name));
        setResultsHighlightTerms(newHighlightTerms);
        
        const hasActiveFilters = !!(
          searchFilters.searchQuery?.trim() ||
          (searchFilters.countries && searchFilters.countries.length > 0) ||
          (searchFilters.companies && searchFilters.companies.length > 0) ||
          (searchFilters.yearOfExperience && (searchFilters.yearOfExperience.min !== undefined || searchFilters.yearOfExperience.max !== undefined)) ||
          (searchFilters.hourlyRate && (searchFilters.hourlyRate.min !== undefined || searchFilters.hourlyRate.max !== undefined)) ||
          (searchFilters.education && searchFilters.education.length > 0) ||
          searchFilters.school?.trim() ||
          searchFilters.certificates ||
          (searchFilters.skills && searchFilters.skills.length > 0) ||
          searchFilters.researchPapersCount !== undefined ||
          (searchFilters.naturalLanguages && searchFilters.naturalLanguages.length > 0) ||
          (searchFilters.programmingLanguages && searchFilters.programmingLanguages.length > 0)
        );

        // Mark search tab as loaded and store filters hash
        tabStatesRef.current['search'].loaded = true;
        tabStatesRef.current['search'].filtersHash = hashFilters(searchFilters);

        // Mixpanel: register domain as super property & track search event
        const currentDomain = searchFilters.domain || 'SWE';
        registerSuperProperties({ domain: currentDomain });
        trackEvent('Search Performed', {
          query: searchFilters.searchQuery || '',
          domain: currentDomain,
          resultCount: response.pagination?.total ?? response.engineers.length,
          hasFilters: hasActiveFilters,
        });
        
        // Store parsed request body for page 2+ (to avoid re-running GPT parsing)
        if (parsed) {
          setParsedRequestBody(parsed);
          
          // Hydrate whenever the user typed a free-text query. The backend now ALWAYS
          // keeps `query` in the request (for ES relevance), so we must NOT gate on it
          // being "consumed" (!parsed.query) — that condition is now always false and
          // would skip hydration entirely. appliedFilters carries the merged result.
          const wasQueryParsed = !!(searchFilters.searchQuery && searchFilters.searchQuery.trim() !== '');
          
          // Full hydration: reflect EVERYTHING the backend applied (frontend filters
          // merged with what the LLM extracted) into the filter panel + URL, so the UI
          // mirrors the resolved NL query. Prefer the clean `appliedFilters`; the
          // hydration runs whenever the query was parsed into structured filters.
          const applied = response.appliedFilters;
          if (wasQueryParsed && applied) {
            const nonEmpty = <T,>(arr?: T[]) => (arr && arr.length > 0 ? arr : undefined);
            const updatedFilters: SearchFilters = {
              ...searchFilters,
              page: 1,
              pageSize: searchFilters.pageSize ?? 18,
              // Replace facets wholesale with what was actually applied (clears stale).
              skills: nonEmpty(applied.skills),
              countries: nonEmpty(applied.countries),
              naturalLanguages: nonEmpty(applied.naturalLanguages),
              programmingLanguages: nonEmpty(applied.programmingLanguages),
              companies: nonEmpty(applied.companies),
              education: nonEmpty(applied.education) as EducationLevel[] | undefined,
              school: applied.schools && applied.schools.length > 0 ? applied.schools[0] : undefined,
              certificates: applied.certificates || undefined,
              yearOfExperience: applied.yearOfExperience || undefined,
              hourlyRate: applied.hourlyRate || undefined,
              researchPapersCount: applied.researchPapersCount,
              typeOfWork: applied.typeOfWork || undefined,
              field: applied.field || undefined,
              domainFilter: applied.domainFilter || undefined,
              // Extended / temporal facets.
              pastCompanies: nonEmpty(applied.pastCompanies),
              currentCompanies: nonEmpty(applied.currentCompanies),
              titles: nonEmpty(applied.titles),
              hasGithubData: applied.hasGithubData || undefined,
              // Negation facets.
              countriesExclude: nonEmpty(applied.countriesExclude),
              companiesExclude: nonEmpty(applied.companiesExclude),
              skillsExclude: nonEmpty(applied.skillsExclude),
              // Exclude action facets — drive the EXCLUDE toggles in the filter panel.
              excludeViewed: applied.excludeViewed || undefined,
              excludeSaved: applied.excludeSaved || undefined,
              excludeDiscarded: applied.excludeDiscarded || undefined,
              // Inclusion action facets (e.g. "already viewed engineers" → onlyViewed).
              onlyViewed: applied.onlyViewed || undefined,
              onlySaved: applied.onlySaved || undefined,
              onlyDiscarded: applied.onlyDiscarded || undefined,
            };

            // excludeViewed/excludeSaved/excludeDiscarded stay on the SEARCH screen and
            // drive the EXCLUDE toggles in FilterSection; the URL carries them via
            // updateUrlWithFilters. No tab switching.
            isUpdatingFromParsedRef.current = true;
            setFilters(updatedFilters);
            updateUrlWithFilters(updatedFilters);

            // We're on the search tab — make sure no stale ?tab= keeps the user off it.
            const cleanParams = new URLSearchParams(window.location.search);
            if (cleanParams.has('tab')) {
              cleanParams.delete('tab');
              const cleanQs = cleanParams.toString();
              window.history.replaceState({}, '', cleanQs ? `${window.location.pathname}?${cleanQs}` : window.location.pathname);
            }
          }
        }
      } else {
        // Only update error state if still on search tab
        if (activeTabRef.current === 'search') {
          setError(response.message || 'Search failed');
          setTalent([]);
          setPagination(undefined);
        }
      }
    } catch (err: unknown) {
      if (signal?.aborted || activeTabRef.current !== 'search') return;
      setError(err instanceof Error ? err.message : 'An error occurred while searching');
      setTalent([]);
      setPagination(undefined);
    } finally {
      // Only clear loading if still on search tab
      if (!signal?.aborted && activeTabRef.current === 'search') setLoading(false);
    }
  }, []);
  
  // Handle tab changes - load/restore tab data when tab changes
  useEffect(() => {
    // Skip on initial mount (handled by initial fetch effect)
    if (prevTabRef.current === null) {
      prevTabRef.current = activeTab;
      return;
    }
    
    // Skip if tab hasn't changed
    if (prevTabRef.current === activeTab) {
      return;
    }
    
    prevTabRef.current = activeTab;
    
    const targetState = tabStatesRef.current[activeTab];

    // For search tab, check if filters have changed since last load
    const shouldReload = activeTab === 'search' 
      ? targetState.filtersHash !== hashFilters(filters)
      : false;

    // If target tab has been loaded and doesn't need reload, restore its state
    if (targetState.loaded && !shouldReload) {
      restoreTabState(activeTab);
      // For non-search tabs, verify data in background and update if different
      if (activeTab !== 'search') {
        refreshTabDataInBackground(activeTab);
      }
    } else {
      // Clear state and load fresh data
      setTalent([]);
      setPagination(undefined);
      setError(null);
      setLoadingMore(false);
      window.scrollTo(0, 0);

      if (activeTab === 'search') {
        // Trigger search with current filters
        const updatedFilters = { ...filters, page: 1 };
        setFilters(updatedFilters);
        performSearch(updatedFilters);
      } else {
        loadTabData(activeTab, 1);
      }
    }
  }, [activeTab, filters, loadTabData, restoreTabState, refreshTabDataInBackground, performSearch]);

  const loadMore = useCallback(async () => {
    if (loading || loadingMore || !pagination || pagination.page >= pagination.totalPages) return;
    if (activeTabRef.current !== 'search') return; // Only for search tab
    
    setLoadingMore(true);
    try {
      const nextPage = pagination.page + 1;
      
      // For page 2+, use parsed request body if available (to avoid re-running GPT parsing)
      // Otherwise fall back to current filters
      let payloadFilters: SearchFilters;
      if (parsedRequestBody) {
        // Use parsed filters from page 1 response
        payloadFilters = {
          domain: parsedRequestBody.domain ?? filters.domain,
          countries: parsedRequestBody.countries,
          yearOfExperience: parsedRequestBody.yearOfExperience,
          education: parsedRequestBody.education as any,
          school: parsedRequestBody.school,
          certificates: parsedRequestBody.certificates,
          skills: parsedRequestBody.skills,
          researchPapersCount: parsedRequestBody.researchPapersCount,
          naturalLanguages: parsedRequestBody.naturalLanguages,
          programmingLanguages: parsedRequestBody.programmingLanguages,
          companies: parsedRequestBody.companies,
          typeOfWork: parsedRequestBody.typeOfWork ?? filters.typeOfWork,
          field: parsedRequestBody.field ?? filters.field,
          domainFilter: parsedRequestBody.domainFilter ?? filters.domainFilter,
          page: nextPage,
          pageSize: parsedRequestBody.pageSize ?? filters.pageSize ?? 18,
          searchQuery: parsedRequestBody.query,
          excludeViewed: parsedRequestBody.excludeViewed ?? filters.excludeViewed,
          excludeSaved: parsedRequestBody.excludeSaved ?? filters.excludeSaved,
          excludeDiscarded: parsedRequestBody.excludeDiscarded ?? filters.excludeDiscarded,
          onlyViewed: parsedRequestBody.onlyViewed ?? filters.onlyViewed,
          onlySaved: parsedRequestBody.onlySaved ?? filters.onlySaved,
          onlyDiscarded: parsedRequestBody.onlyDiscarded ?? filters.onlyDiscarded,
          pastCompanies: parsedRequestBody.pastCompanies ?? filters.pastCompanies,
          currentCompanies: parsedRequestBody.currentCompanies ?? filters.currentCompanies,
          titles: parsedRequestBody.titles ?? filters.titles,
          hasGithubData: parsedRequestBody.hasGithubData ?? filters.hasGithubData,
          countriesExclude: parsedRequestBody.countriesExclude ?? filters.countriesExclude,
          companiesExclude: parsedRequestBody.companiesExclude ?? filters.companiesExclude,
          skillsExclude: parsedRequestBody.skillsExclude ?? filters.skillsExclude,
          hourlyRate: parsedRequestBody.hourlyRate ?? filters.hourlyRate,
        };
      } else {
        payloadFilters = { ...filters, page: nextPage };
      }
      
      const payload = buildSearchPayload(payloadFilters);
      if (selectedBatchRef.current) {
        payload.packetIds = [selectedBatchRef.current.id];
      } else if (allPacketIdsRef.current.length > 0) {
        payload.packetIds = allPacketIdsRef.current;
      }
      const response = await searchEngineers(payload);

      // Check if we're still on search tab (user may have switched during API call)
      if (activeTabRef.current !== 'search') return;
      
      if (response.success) {
        // Show all engineers including discarded (discarded will be greyed out), deduplicate by id
        setTalent(prev => {
          const existingIds = new Set(prev.map(t => t.id));
          const newTalent = response.engineers.filter(e => !existingIds.has(e.id));
          return [...prev, ...newTalent];
        });
        setPagination(response.pagination);
        
        // Update saved/viewed/discarded IDs from response
        response.engineers.forEach(e => {
          if (e.isSaved) setSavedIds(prev => new Set(prev).add(e.id));
          if (e.isViewed) setViewedIds(prev => new Set(prev).add(e.id));
          if (e.isDiscarded) setDiscardedIds(prev => new Set(prev).add(e.id));
        });
      }
    } catch {
      // Keep existing results on load-more failure
    } finally {
      // Always clear loadingMore (even if tab changed, we need to reset the spinner)
      setLoadingMore(false);
    }
  }, [filters, pagination, loading, loadingMore, parsedRequestBody]);

  // Track when we're updating filters from parsed results to prevent re-search
  const isUpdatingFromParsedRef = useRef(false);
  // Track if search should be triggered (by filter chip changes, not by typing)
  const shouldTriggerSearchRef = useRef(false);

  // Search when filter chips change (not when typing in search bar)
  useEffect(() => {
    if (shouldTriggerSearchRef.current) {
      shouldTriggerSearchRef.current = false;
      performSearch(filters);
    }
  }, [filters, performSearch]);

  // Page changes are handled in the main auto-search useEffect above

  const handleFilterChange = (newFilters: SearchFilters) => {
    const updatedFilters = { ...newFilters, page: 1 };
    // Mark that this filter change should trigger a search
    shouldTriggerSearchRef.current = true;
    setFilters(updatedFilters);
    updateUrlWithFilters(updatedFilters);
    // Clear parsed request body when user manually changes filters
    // This ensures loadMore uses the current filters, not stale parsed ones
    setParsedRequestBody(null);
  };

  // Scroll to top when filters change (but don't show loading immediately - wait for API call)
  // Skip when updating from parsed results since we already have the data
  const prevFiltersRef = useRef<SearchFilters | null>(null);
  useEffect(() => {
    if (prevFiltersRef.current === null) {
      prevFiltersRef.current = filters;
      return;
    }
    const prevFilters = prevFiltersRef.current;
    prevFiltersRef.current = filters;
    // Don't scroll when updating from parsed results (we already have data)
    if (isUpdatingFromParsedRef.current) {
      isUpdatingFromParsedRef.current = false; // Reset the flag after consuming
      return;
    }
    // Don't scroll when user is just typing in search box
    // (typing also clears skills/countries/naturalLanguages, so we check if query changed)
    const isTypingInSearchBox = prevFilters.searchQuery !== filters.searchQuery;
    if (isTypingInSearchBox) {
      return; // Don't scroll while typing - scroll will happen when results load
    }
    // Scroll to top for other filter changes (e.g., selecting a country, skill, etc.)
    window.scrollTo(0, 0);
    // Loading is now set only when API is actually called (in performSearch)
  }, [filters]);
  
  const handleSearch = () => {
    // This is now optional - filters auto-search, but keeping for explicit trigger if needed
    const updatedFilters = { ...filters, page: 1 };
    setFilters(updatedFilters);
    updateUrlWithFilters(updatedFilters);
  };

  const handleLogoClick = () => {
    const defaultFilters: SearchFilters = { page: 1, pageSize: 18, domain: selectableDomains[0] || availableDomains[0] || 'SWE' };
    setFilters(defaultFilters);
    setParsedRequestBody(null);
    // Switch to search tab and reset
    if (activeTab !== 'search') {
      setActiveTab('search');
    }
    updateUrlWithFilters(defaultFilters, true);
    performSearch(defaultFilters);
  };

  const handleFirstInteraction = () => {
    if (!hasInteracted) {
      setHasInteracted(true);
    }
  };

  const getActiveFilterCount = (): number => {
    let count = 0;
    if (filters.searchQuery && filters.searchQuery.trim().length > 0) count++;
    if (filters.domain && filters.domain !== (selectableDomains[0] || availableDomains[0] || 'SWE')) count++;
    if (filters.countries && filters.countries.length > 0) count++;
    if (filters.companies && filters.companies.length > 0) count++;
    if (filters.yearOfExperience && (filters.yearOfExperience.min !== undefined || filters.yearOfExperience.max !== undefined)) count++;
    if (filters.education && filters.education.length > 0) count++;
    if (filters.school && filters.school.trim().length > 0) count++;
    if (filters.certificates) count++;
    if (filters.skills && filters.skills.length > 0) count++;
    if (filters.researchPapersCount !== undefined) count++;
    if (filters.naturalLanguages && filters.naturalLanguages.length > 0) count++;
    if (filters.programmingLanguages && filters.programmingLanguages.length > 0) count++;
    if (filters.typeOfWork && filters.typeOfWork.length > 0) count++;
    return count;
  };


  // Handle scroll-based title minimization with smooth transition
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const scrollThreshold = 50; // Start minimizing after 50px scroll
      
      // Smooth transition based on scroll position
      if (currentScrollY > scrollThreshold) {
        setHasInteracted(true); // Mark as interacted when scrolling
      }
      
      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY, hasInteracted]);

  // Handle browser back/forward navigation
  useEffect(() => {
    const handlePopState = () => {
      const urlFilters = deserializeFiltersFromUrl();
      setFilters(urlFilters);
      setParsedRequestBody(null); // Clear parsed filters on browser navigation
      // Search will be triggered automatically via useEffect
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Sync hasInteracted from URL on mount only (filters already initialized from URL in useState)
  useEffect(() => {
    const urlFilters = deserializeFiltersFromUrl();
    const hasFilters = Object.keys(urlFilters).some(
      (key) => key !== 'page' && key !== 'pageSize' && urlFilters[key as keyof SearchFilters]
    );
    if (hasFilters && Object.keys(urlFilters).length > 2) {
      setHasInteracted(true);
    }
  }, []);

  // Lock body scroll and handle Escape when mobile filter drawer is open
  useEffect(() => {
    if (!filterDrawerOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setFilterDrawerOpen(false);
    };
    document.addEventListener('keydown', onEscape);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener('keydown', onEscape);
    };
  }, [filterDrawerOpen]);

  useEffect(() => {
    if (activeTab === 'saved' && !shareId) {
      getShareLink().then(({ shareId: id }) => setShareId(id)).catch(() => {});
    }
  }, [activeTab, shareId]);

  // Combined loadMore that handles both search and action tabs
  const handleLoadMore = useCallback(async (domain?: string) => {
    if (activeTab === 'search') {
      await loadMore();
    } else if (domain) {
      await loadMoreTabData(domain);
    }
  }, [activeTab, loadMore, loadMoreTabData]);

  const handleCardClick = useCallback((clickedTalent: SoftwareTalent, domainFilter?: string) => {
    setQuickViewTalent(clickedTalent);
    if (domainFilter) {
      setActiveDomainFilter(domainFilter);
    }
    trackEvent('Quick View Opened', { candidateId: clickedTalent.id });
    startTimedEvent('Quick View Engagement');
  }, []);

  const handleQuickViewClose = useCallback(() => {
    if (quickViewTalent) {
      trackEvent('Quick View Engagement', { candidateId: quickViewTalent.id });
    }
    setQuickViewTalent(null);
  }, [quickViewTalent]);


  const handleNavigateTalent = useCallback((newTalent: SoftwareTalent) => {
    setQuickViewTalent(newTalent);
  }, []);

  // Scroll background listing to keep the active QuickView talent card visible
  useEffect(() => {
    if (!quickViewTalent) return;
    const card = document.querySelector(`[data-talent-id="${quickViewTalent.id}"]`);
    if (card) {
      card.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [quickViewTalent]);

  // Handle save from inline detail
  const handleDetailSave = useCallback((talentId: number, domain?: DomainType) => {
    handleSave(talentId, domain);
  }, [handleSave]);

  // Handle unsave from inline detail
  const handleDetailUnsave = useCallback((talentId: number, domain?: DomainType) => {
    handleUnsave(talentId, domain);
  }, [handleUnsave]);

  // Handle discard from inline detail
  const handleDetailDiscard = useCallback((talentId: number, domain?: DomainType) => {
    handleDiscard(talentId, domain);
  }, [handleDiscard]);

  // Handle restore from inline detail
  const handleDetailRestore = useCallback((talentId: number, domain?: DomainType) => {
    handleRestore(talentId, domain);
  }, [handleRestore]);

  // Compute hasMore per domain for action tabs based on page tracking
  const currentTabForPages = activeTab === 'saved' || activeTab === 'viewed' || activeTab === 'discarded' ? activeTab : 'saved';
  const hasMoreByDomain: Record<string, boolean> = Object.fromEntries(
    availableDomains.map(d => [
      d,
      (lastPageLoaded[currentTabForPages]?.[d] ?? 1) < (totalPages[currentTabForPages]?.[d] ?? 1),
    ])
  );

  // Compute countsByDomain for the current action tab (for SearchResults pill tabs)
  const countsByDomain: Record<string, number> = Object.fromEntries(
    availableDomains.map(d => [
      d,
      activeTab === 'saved' ? (domainCounts[d]?.saved ?? 0) :
      activeTab === 'viewed' ? (domainCounts[d]?.viewed ?? 0) :
      activeTab === 'discarded' ? (domainCounts[d]?.discarded ?? 0) : 0,
    ])
  );

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-950 flex flex-col">
      <div className="flex-1 flex flex-col">
        {/* Navigation Bar */}
        <AppNavbar
          activeTab={activeTab}
          onTabChange={handleTabChange}
          onLogoClick={handleLogoClick}
          showTabs={true}
          savedCount={savedCount}
          viewedCount={viewedCount}
          discardedCount={discardedCount}
          isVisible={isNavbarVisible}
          // temporarily hidden — default to all batches view
          // leftContent={batches dropdown}
        />

        <div className="max-w-[1600px] mx-auto w-full flex-1 flex flex-col">
          {/* Search Bar Section - Only show for search tab */}
          {activeTab === 'search' && (
            <div 
              className={`fixed top-14 left-0 right-0 z-40 bg-gray-50 dark:bg-slate-900 transition-transform duration-300 ease-out ${
                isNavbarVisible ? 'translate-y-0' : '-translate-y-14'
              }`}
            >
              <div className="max-w-[1600px] mx-auto px-3 sm:px-4 md:px-6 py-3 border-b border-gray-200 dark:border-gray-800">
              <div className="flex items-center gap-3">
                {/* Search Input with Domain Dropdown on left and Search button on right */}
                <div className="flex-1 flex items-center gap-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-slate-900 p-2 shadow-sm hover:shadow-md transition-shadow">
                  {/* Domain selector on the left - hidden on mobile, only shown when multiple domains are available */}
                  {multiDomain && (
                    <div className="relative flex-shrink-0 hidden sm:block" ref={domainDropdownDesktopRef}>
                      <button
                        type="button"
                        onClick={() => setDomainDropdownOpen(prev => !prev)}
                        className="flex items-center gap-1.5 bg-gray-100 dark:bg-gray-800 text-sm font-medium text-gray-900 dark:text-gray-100 pl-3 pr-2 py-2 rounded-lg cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors focus:outline-none"
                      >
                        {getDomainLabel(filters.domain || selectableDomains[0] || 'SWE')}
                        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${domainDropdownOpen ? 'rotate-180' : ''}`} />
                      </button>
                      {domainDropdownOpen && (
                        <div className="absolute top-full left-0 mt-1 min-w-[200px] bg-white dark:bg-slate-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg py-1 z-50">
                          {selectableDomains.map((d) => {
                            const isSelected = (filters.domain || selectableDomains[0] || 'SWE') === d;
                            return (
                              <button
                                key={d}
                                type="button"
                                onClick={() => {
                                  trackEvent('Domain Switched', { fromDomain: filters.domain || selectableDomains[0] || 'SWE', toDomain: d });
                                  const resetFilters = {
                                    domain: d,
                                    page: 1,
                                    pageSize: 18,
                                  };
                                  setLoading(true);
                                  setTalent([]);
                                  setFilters(resetFilters);
                                  updateUrlWithFilters(resetFilters);
                                  setParsedRequestBody(null);
                                  setHasInteracted(true);
                                  setDomainDropdownOpen(false);
                                }}
                                className={`w-full text-left px-3 py-2 text-sm transition-colors ${
                                  isSelected
                                    ? 'bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100 font-medium'
                                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/50'
                                }`}
                              >
                                {getDomainLabel(d)}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Search input */}
                  <div className="relative flex items-center gap-2 flex-1 min-w-0 py-1 sm:py-0">
                    <Search className="w-5 h-5 text-gray-400 dark:text-gray-500 flex-shrink-0" />
                    <input
                      type="text"
                      className="flex-1 min-w-0 bg-transparent border-none outline-none text-base sm:text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500"
                      placeholder={getDomainPlaceholder(filters.domain || selectableDomains[0] || 'SWE')}
                      value={filters.searchQuery || ''}
                      onChange={(e) => {
                        const newQuery = e.target.value;
                        
                        // Update local state immediately for responsive UI
                        setFilters(prev => ({ 
                          ...prev, 
                          searchQuery: newQuery, 
                          page: 1,
                        }));
                        setHasInteracted(true);
                        
                        // Clear any existing debounce timer
                        if (debounceTimerRef.current) {
                          clearTimeout(debounceTimerRef.current);
                        }
                        
                        // Set up debounced auto-search (1500ms)
                        debounceTimerRef.current = setTimeout(() => {
                          // New query → clean slate; the new parse fully determines facets.
                          const searchFilters = { ...resetParsedFilters(filters), searchQuery: newQuery };
                          setFilters(searchFilters);
                          updateUrlWithFilters(searchFilters);
                          setParsedRequestBody(null);
                          performSearch(searchFilters);
                        }, 1500);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          // Clear debounce timer on Enter (immediate search)
                          if (debounceTimerRef.current) {
                            clearTimeout(debounceTimerRef.current);
                            debounceTimerRef.current = null;
                          }
                          // New query → clean slate; the new parse fully determines facets.
                          const searchFilters = { ...resetParsedFilters(filters), searchQuery: filters.searchQuery };
                          setFilters(searchFilters);
                          updateUrlWithFilters(searchFilters);
                          setParsedRequestBody(null);
                          performSearch(searchFilters);
                        }
                      }}
                      onFocus={() => setHasInteracted(true)}
                    />
                  </div>

                  {/* Search Button on the right - hidden on mobile */}
                  <button
                    onClick={() => {
                      // Clear debounce timer on button click (immediate search)
                      if (debounceTimerRef.current) {
                        clearTimeout(debounceTimerRef.current);
                        debounceTimerRef.current = null;
                      }
                      // New query → clean slate; the new parse fully determines facets.
                      const searchFilters = { ...resetParsedFilters(filters), searchQuery: filters.searchQuery };
                      setFilters(searchFilters);
                      updateUrlWithFilters(searchFilters);
                      setParsedRequestBody(null);
                      performSearch(searchFilters);
                    }}
                    className="hidden sm:flex items-center justify-center gap-2 px-4 py-2 bg-gray-900 dark:bg-gray-100 hover:bg-gray-800 dark:hover:bg-gray-200 text-white dark:text-gray-900 text-sm font-medium rounded-lg transition-colors"
                  >
                    <Search className="w-4 h-4" />
                    <span>Search</span>
                  </button>
                </div>

                {/* Mobile Domain selector - only shown when multiple domains are available */}
                {multiDomain && (
                  <div className="relative sm:hidden flex-shrink-0" ref={domainDropdownMobileRef}>
                    <button
                      type="button"
                      onClick={() => setDomainDropdownOpen(prev => !prev)}
                      className="flex items-center gap-1 bg-white dark:bg-slate-900 border border-gray-200 dark:border-gray-700 text-sm font-medium text-gray-900 dark:text-gray-100 pl-2 pr-1.5 py-2 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors focus:outline-none"
                    >
                      {getDomainLabel(filters.domain || selectableDomains[0] || 'SWE')}
                      <ChevronDown className={`w-3.5 h-3.5 text-gray-400 transition-transform ${domainDropdownOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {domainDropdownOpen && (
                      <div className="absolute top-full right-0 mt-1 min-w-[180px] bg-white dark:bg-slate-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg py-1 z-50">
                        {selectableDomains.map((d) => {
                          const isSelected = (filters.domain || selectableDomains[0] || 'SWE') === d;
                          return (
                            <button
                              key={d}
                              type="button"
                              onClick={() => {
                                trackEvent('Domain Switched', { fromDomain: filters.domain || selectableDomains[0] || 'SWE', toDomain: d });
                                const resetFilters = {
                                  domain: d,
                                  page: 1,
                                  pageSize: 18,
                                };
                                setLoading(true);
                                setTalent([]);
                                setFilters(resetFilters);
                                updateUrlWithFilters(resetFilters);
                                setParsedRequestBody(null);
                                setHasInteracted(true);
                                setDomainDropdownOpen(false);
                              }}
                              className={`w-full text-left px-3 py-2 text-sm transition-colors ${
                                isSelected
                                  ? 'bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100 font-medium'
                                  : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/50'
                              }`}
                            >
                              {getDomainLabel(d)}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

                {/* Mobile Filter Button */}
                <button
                  onClick={() => setFilterDrawerOpen(true)}
                  className="lg:hidden flex items-center justify-center w-10 h-10 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-slate-900 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors"
                  aria-label="Open filters"
                >
                  <SlidersHorizontal className="w-4 h-4" />
                  {getActiveFilterCount() > 0 && (
                    <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 text-[10px] font-medium flex items-center justify-center">
                      {getActiveFilterCount()}
                    </span>
                  )}
                </button>
              </div>

              {/* Results count - shown on desktop */}
              <div className="hidden lg:flex items-center justify-between mt-3">
                <div className="flex items-baseline gap-2">
                  <span className="font-medium text-sm text-gray-900 dark:text-gray-100">
                    {loading
                      ? 'Searching...'
                      : (() => {
                          const count = pagination?.total ?? talent.length;
                          if (count === 0) return 'No results';
                          if (count < 10) return `${count} ${count === 1 ? 'candidate' : 'candidates'}`;
                          const step = count >= 100 ? 100 : 10;
                          const rounded = Math.floor(count / step) * step;
                          return `${rounded.toLocaleString()}+ candidates`;
                        })()}
                  </span>
                  {pagination && !loading && (
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {getActiveFilterCount() > 0 ? '· Sorted by match' : '· Sorted by relevance'}
                    </span>
                  )}
                </div>
              </div>
              </div>
            </div>
          )}

          {/* Spacer for fixed search bar */}
          {activeTab === 'search' && (
            <div className="h-[88px] lg:h-[100px]" />
          )}

          {/* Saved tab sticky header */}
          {activeTab === 'saved' && (
            <>
              <div className={`fixed top-14 left-0 right-0 z-40 bg-zinc-50 dark:bg-zinc-950 border-b border-zinc-200 dark:border-zinc-800 px-4 md:px-6 py-4 transition-transform duration-300 ease-out ${isNavbarVisible ? 'translate-y-0' : '-translate-y-14'}`}>
                <div className="max-w-[1600px] mx-auto flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => handleTabChange('search')}
                      className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium bg-zinc-100 dark:bg-zinc-800 text-zinc-800 dark:text-zinc-200 hover:bg-zinc-200 dark:hover:bg-zinc-700 rounded-lg transition-colors"
                    >
                      <ArrowLeft className="w-4 h-4" aria-hidden="true" />
                      <span className="hidden sm:inline">Back to search</span>
                    </button>
                    <div className="h-4 w-px bg-zinc-200 dark:bg-zinc-700 hidden sm:block" />
                    <span className="text-sm font-medium text-zinc-600 dark:text-zinc-400">{savedCount} saved</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {canDownload && (
                      <button
                        onClick={async () => {
                          if (downloadingSavedCsv) return;
                          setDownloadingSavedCsv(true);
                          try {
                            const count = await exportSavedProfilesCsv(availableDomains as DomainType[]);
                            trackEvent('saved_csv_downloaded', { count });
                          } catch {
                            alert('Failed to download CSV. Please try again.');
                          } finally {
                            setDownloadingSavedCsv(false);
                          }
                        }}
                        disabled={savedCount === 0 || downloadingSavedCsv}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 text-zinc-700 dark:text-zinc-200 hover:bg-zinc-50 dark:hover:bg-zinc-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {downloadingSavedCsv ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> : <Download className="w-4 h-4" aria-hidden="true" />}
                        {downloadingSavedCsv ? 'Preparing…' : 'Download'}
                      </button>
                    )}
                    <button
                      onClick={async () => {
                        if (!shareId) return;
                        try {
                          const url = `${window.location.origin}/list/${shareId}`;
                          await navigator.clipboard.writeText(url);
                          setShareCopied(true);
                          setTimeout(() => setShareCopied(false), 2000);
                        } catch {
                          // ignore
                        }
                      }}
                      disabled={savedCount === 0 || !shareId}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900 rounded-lg hover:bg-zinc-800 dark:hover:bg-zinc-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {shareCopied ? <Check className="w-4 h-4" aria-hidden="true" /> : <Share className="w-4 h-4" aria-hidden="true" />}
                      {shareCopied ? 'Copied!' : 'Share'}
                    </button>
                  </div>
                </div>
              </div>
              <div className="h-[60px]" />
            </>
          )}

          {/* Mobile: filter drawer from left - Only for search tab */}
          {activeTab === 'search' && (
            <Sheet open={filterDrawerOpen} onOpenChange={setFilterDrawerOpen}>
              <SheetContent side="left" className="w-[85%] max-w-sm p-0 flex flex-col">
                <SheetHeader className="px-4 py-3 border-b border-border flex-shrink-0">
                  <SheetTitle>Filters</SheetTitle>
                </SheetHeader>
                <div className="flex-1 min-h-0 flex flex-col overflow-y-auto">
                  <FilterSection
                    filters={filters}
                    onFilterChange={handleFilterChange}
                    onApplyFilters={() => {
                      handleSearch();
                      setFilterDrawerOpen(false);
                    }}
                    onInteraction={handleFirstInteraction}
                    fillHeight
                    configReady={domainsReady}
                  />
                </div>
                <div className="flex-shrink-0 p-4 border-t border-border">
                  <Button
                    className="w-full"
                    onClick={() => {
                      handleSearch();
                      setFilterDrawerOpen(false);
                    }}
                  >
                    Apply & close
                  </Button>
                </div>
              </SheetContent>
            </Sheet>
          )}

          {/* Main Content */}
          <div className={`flex flex-col lg:flex-row gap-4 sm:gap-6 items-stretch lg:items-start px-3 sm:px-4 md:px-6 py-6 sm:py-8 flex-1 min-w-0`}>
            {/* Filter Panel - Fixed on left side, only show for search tab */}
            {activeTab === 'search' && (
              <>
                {/* Fixed filters sidebar */}
                <aside 
                  className={`hidden lg:block fixed left-0 w-80 z-30 top-[188px] transition-all duration-300 ease-out ${
                    isNavbarVisible ? 'translate-y-0' : '-translate-y-14'
                  }`}
                  style={{ 
                    marginLeft: 'max(0px, calc((100vw - 1600px) / 2))',
                    paddingLeft: '24px'
                  }}
                >
                  <div className={`flex flex-col transition-[max-height] duration-300 ease-out ${
                    isNavbarVisible ? 'max-h-[calc(100vh-212px)]' : 'max-h-[calc(100vh-156px)]'
                  }`}>
                    <FilterSection
                      filters={filters}
                      onFilterChange={handleFilterChange}
                      onApplyFilters={handleSearch}
                      onInteraction={handleFirstInteraction}
                      configReady={domainsReady}
                    />
                  </div>
                </aside>
                {/* Spacer for fixed filters */}
                <div className="hidden lg:block lg:w-80 flex-shrink-0" />
              </>
            )}

            {/* Results */}
            <main className="flex flex-1 w-full min-w-0 flex-col">
                {error && (
                  <div className="mb-4">
                    <div className="flex items-center gap-3 px-4 py-3 rounded-lg border border-red-200 dark:border-red-900/50 bg-red-50 dark:bg-red-950/30 text-red-700 dark:text-red-300">
                      <AlertCircle className="w-5 h-5 flex-shrink-0 text-red-500 dark:text-red-400" />
                      <span className="text-sm font-medium flex-1">{error}</span>
                      <button
                        onClick={() => setError(null)}
                        className="p-1 rounded-md text-red-400 dark:text-red-500 hover:text-red-600 dark:hover:text-red-300 hover:bg-red-100 dark:hover:bg-red-900/40 transition-colors"
                        aria-label="Dismiss error"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )}
                <SearchResults
                  talent={talent}
                  loading={activeTab === 'search' ? loading : (loading && talent.length === 0)}
                  loadingMore={loadingMore}
                  hasMore={!!pagination && pagination.page < pagination.totalPages}
                  onLoadMore={handleLoadMore}
                  filters={filters}
                  onFiltersChange={setFilters}
                  pagination={pagination}
                  resultsHighlightTerms={resultsHighlightTerms}
                  savedIds={savedIds}
                  viewedIds={viewedIds}
                  discardedIds={discardedIds}
                  onSave={handleSave}
                  onUnsave={handleUnsave}
                  onDiscard={handleDiscard}
                  onRestore={handleRestore}
                  activeTab={activeTab}
                  onCardClick={handleCardClick}
                  defaultDomainFilter={filters.domain || selectableDomains[0] || 'SWE'}
                  countsByDomain={countsByDomain}
                  hasMoreByDomain={hasMoreByDomain}
                  availableDomains={selectableDomains}
                  multiDomainEnabled={multiDomain}
                />
            </main>
          </div>
        </div>
      </div>

      {/* QuickViewModal - overlay popup when user clicks a card */}
      {quickViewTalent && (
        <QuickViewModal
          talent={quickViewTalent}
          domain={(quickViewTalent.domain as DomainType) || (filters.domain as DomainType) || 'SWE'}
          allTalent={
            activeTab !== 'search'
              ? talent.filter(t => t.domain === activeDomainFilter)
              : talent
          }
          onClose={handleQuickViewClose}
          onNavigateTalent={handleNavigateTalent}
          onView={handleView}
          onSave={handleDetailSave}
          onUnsave={handleDetailUnsave}
          onDiscard={handleDetailDiscard}
          onRestore={handleDetailRestore}
          isSaved={savedIds.has(quickViewTalent.id)}
          isDiscarded={discardedIds.has(quickViewTalent.id)}
          
          discardedIds={discardedIds}
          hasMore={
            activeTab === 'search'
              ? (!!pagination && pagination.page < pagination.totalPages)
              : (hasMoreByDomain[activeDomainFilter] ?? false)
          }
          onLoadMore={async () => {
            await handleLoadMore(activeTab === 'search' ? undefined : activeDomainFilter);
          }}
          loadingMore={loadingMore}
          activeTab={activeTab}
        />
      )}
    </div>
  );
};

export default SearchPage;
